import "./i18n"
import "./styles/global.css"
import "./styles/typography.scss"
import { StrictMode, Suspense, useSyncExternalStore } from "react"

import { Notifier } from "@airbrake/browser"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { createRoot } from "react-dom/client"
import { HelmetProvider } from "react-helmet-async"
import { BrowserRouter as Router } from "react-router-dom"

import { RecoilFreeErrorBoundary } from "./components/ErrorBoundary"
import Loading from "./components/Loading"
import { AirbrakeContext } from "./contexts"
import EnvVars from "./env-vars"
import { RootSwitch } from "./RootSwitch"
import * as serviceWorker from "./serviceWorker"
import { bootstrapAuthTokensFromUrl } from "./utils/bootstrap-auth-tokens"
import { handleGraphqlError } from "./utils/handleGraphQLError"
import IS_DEV from "./utils/isDev"
import { redactAirbrakeNotice } from "./utils/redact-airbrake-notice"

bootstrapAuthTokensFromUrl()

const createAirbrake = (): Notifier => {
  const notifier = new Notifier({
    projectId: parseInt(EnvVars.AIRBRAKE_PROJECT_ID || `0`, 10),
    projectKey: EnvVars.AIRBRAKE_PROJECT_KEY || ``,
    environment: EnvVars.NODE_ENV,
  })
  notifier.addFilter(redactAirbrakeNotice)

  return notifier
}

const airbrake = {
  airbrake: import.meta.env.VITE_DISABLE_AIRBRAKE ? null : createAirbrake(),
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((error as any).response?.errors?.[0].code === 401) {
            return false
          }
        } catch (err) {
          console.error(err)
        }

        return failureCount !== 3
      },
      onError: handleGraphqlError,
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh for this duration
      cacheTime: 10 * 60 * 1000, // 10 minutes - cache retention (renamed to gcTime in v5)
    },
    mutations: {
      onError: handleGraphqlError,
    },
  },
})

const DESKTOP_MEDIA_QUERY = `(min-width: 768px)`

const subscribeToDesktopViewport = (callback: () => void): (() => void) => {
  const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY)
  mediaQuery.addEventListener(`change`, callback)

  return () => {
    mediaQuery.removeEventListener(`change`, callback)
  }
}

const getDesktopViewportSnapshot = (): boolean => {
  return window.matchMedia(DESKTOP_MEDIA_QUERY).matches
}

const ReactQueryDevtoolsDesktop = (): JSX.Element | null => {
  const isDesktop = useSyncExternalStore(
    subscribeToDesktopViewport,
    getDesktopViewportSnapshot,
    () => false,
  )

  if (!isDesktop) {
    return null
  }

  return <ReactQueryDevtools position="top-right" initialIsOpen={false} />
}

const container = document.getElementById(`root`)
const root = createRoot(container as Element)
root.render(
  <StrictMode>
    <AirbrakeContext.Provider value={airbrake}>
      <RecoilFreeErrorBoundary>
        <Suspense
          fallback={
            <div
              style={{
                display: `flex`,
                justifyContent: `center`,
                alignItems: `center`,
                height: `100vh`,
              }}
            >
              <Loading />
            </div>
          }
        >
          <QueryClientProvider client={queryClient}>
            <HelmetProvider>
              <Router>
                <RootSwitch />
              </Router>
            </HelmetProvider>
            {IS_DEV ? <ReactQueryDevtoolsDesktop /> : null}
          </QueryClientProvider>
        </Suspense>
      </RecoilFreeErrorBoundary>
    </AirbrakeContext.Provider>
  </StrictMode>,
)
// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
